import { db } from '@/lib/firebase'
import type { SupportMessage, SupportMessageInput } from '@/types/support'
import {
  type Timestamp,
  addDoc,
  collection,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
  where,
} from 'firebase/firestore'

const SUPPORT_COLLECTION = 'support'
const NOTIFICATIONS_COLLECTION = 'notifications'

export const supportService = {
  // Create a new support message
  async createSupportMessage(data: SupportMessageInput): Promise<string> {
    try {
      const docRef = await addDoc(collection(db, SUPPORT_COLLECTION), {
        ...data,
        status: 'open',
        priority: data.priority || 'medium',
        source: data.source || 'support', // Default to 'support' if not specified
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      })
      return docRef.id
    } catch (error) {
      console.error('Error creating support message:', error)
      throw error
    }
  },

  // Get all support messages (for super admins)
  async getAllSupportMessages(): Promise<SupportMessage[]> {
    try {
      const q = query(collection(db, SUPPORT_COLLECTION), orderBy('createdAt', 'desc'))
      const querySnapshot = await getDocs(q)

      const messages: SupportMessage[] = []
      for (const docSnap of querySnapshot.docs) {
        const data = docSnap.data()

        // Get user details
        let userName = data.userName
        let userAvatar = data.userAvatar
        let companyName = data.companyName

        if (!userName || !userAvatar || !companyName) {
          try {
            // Get user data
            const userDoc = await getDoc(doc(db, 'users', data.userId))
            if (userDoc.exists()) {
              const userData = userDoc.data()
              userName = userData.fullName || data.userEmail
              userAvatar = userData.profilePicUrl

              // Get company data if user has companyId
              if (userData.companyId && !companyName) {
                const companyDoc = await getDoc(doc(db, 'companies', userData.companyId))
                if (companyDoc.exists()) {
                  companyName = companyDoc.data().name
                }
              }
            }
          } catch (userError) {
            console.error('Error fetching user/company data:', userError)
            userName = data.userEmail // Fallback to email
          }
        }

        messages.push({
          id: docSnap.id,
          ...data,
          userName,
          userAvatar,
          companyName,
          createdAt: data.createdAt as Timestamp,
        } as SupportMessage)
      }

      return messages
    } catch (error) {
      console.error('Error fetching support messages:', error)
      throw error
    }
  },

  // Get support messages by user ID
  async getSupportMessagesByUser(userId: string): Promise<SupportMessage[]> {
    try {
      const q = query(collection(db, SUPPORT_COLLECTION), where('userId', '==', userId), orderBy('createdAt', 'desc'))
      const querySnapshot = await getDocs(q)

      return querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt as Timestamp,
      })) as SupportMessage[]
    } catch (error) {
      console.error('Error fetching user support messages:', error)
      throw error
    }
  },

  // Get a specific support message by ID
  async getSupportMessageById(messageId: string): Promise<SupportMessage | null> {
    try {
      const docSnap = await getDoc(doc(db, SUPPORT_COLLECTION, messageId))

      if (!docSnap.exists()) {
        return null
      }

      const data = docSnap.data()

      // Get additional user and company details
      let userName = data.userName
      let userAvatar = data.userAvatar
      let companyName = data.companyName

      if (!userName || !userAvatar || !companyName) {
        try {
          const userDoc = await getDoc(doc(db, 'users', data.userId))
          if (userDoc.exists()) {
            const userData = userDoc.data()
            userName = userData.fullName || data.userEmail
            userAvatar = userData.profilePicUrl

            if (userData.companyId && !companyName) {
              const companyDoc = await getDoc(doc(db, 'companies', userData.companyId))
              if (companyDoc.exists()) {
                companyName = companyDoc.data().name
              }
            }
          }
        } catch (userError) {
          console.error('Error fetching user/company data:', userError)
          userName = data.userEmail
        }
      }

      return {
        id: docSnap.id,
        ...data,
        userName,
        userAvatar,
        companyName,
        createdAt: data.createdAt as Timestamp,
      } as SupportMessage
    } catch (error) {
      console.error('Error fetching support message:', error)
      throw error
    }
  },

  // Create notification for support status update
  async createSupportNotification(
    userId: string,
    supportTicketId: string,
    supportStatus: string,
    supportSubject: string,
  ): Promise<void> {
    try {
      const notificationData = {
        userId,
        type: 'support_status_update',
        title: 'Support Ticket Update',
        message: `Your support ticket "${supportSubject}" has been updated to ${supportStatus}.`,
        data: {
          supportTicketId,
          supportStatus,
          supportSubject,
        },
        read: false,
        createdAt: serverTimestamp(),
      }

      await addDoc(collection(db, NOTIFICATIONS_COLLECTION), notificationData)
    } catch (error) {
      console.error('Error creating support notification:', error)
      // Don't throw error to avoid breaking the main update operation
    }
  },

  // Update support message status
  async updateSupportMessageStatus(
    messageId: string,
    status: SupportMessage['status'],
    adminNotes?: string,
    resolvedBy?: string,
  ): Promise<void> {
    try {
      // First get the current ticket to extract user info for notification
      const currentTicket = await this.getSupportMessageById(messageId)

      const updateData: any = {
        status,
        updatedAt: serverTimestamp(),
      }

      if (adminNotes) {
        updateData.adminNotes = adminNotes
      }

      if (status === 'resolved' || status === 'closed') {
        updateData.resolvedAt = serverTimestamp()
        if (resolvedBy) {
          updateData.resolvedBy = resolvedBy
        }
      }

      await updateDoc(doc(db, SUPPORT_COLLECTION, messageId), updateData)

      // Create notification for the user if ticket exists and status changed
      if (currentTicket?.userId) {
        await this.createSupportNotification(
          currentTicket.userId,
          messageId,
          status || 'updated',
          currentTicket.subject,
        )
      }
    } catch (error) {
      console.error('Error updating support message:', error)
      throw error
    }
  },

  // Add admin notes to a support message
  async addAdminNotes(messageId: string, notes: string): Promise<void> {
    try {
      await updateDoc(doc(db, SUPPORT_COLLECTION, messageId), {
        adminNotes: notes,
        updatedAt: serverTimestamp(),
      })
    } catch (error) {
      console.error('Error adding admin notes:', error)
      throw error
    }
  },
}
